/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export interface Channel {
  id: 'imessage' | 'telegram' | 'slack' | 'dashboard';
  name: string;
  status: 'active' | 'gated' | 'abandoned' | 'inactive';
  receiver: string; // e.g., 'chat.db', 'Bot API', 'Socket Mode'
  hmac: boolean;
  messagesReceived: number;
  messagesSent: number;
}

export interface Agent {
  id: string;
  name: string;
  avatar: string; // symbol or initial
  status: 'online' | 'offline' | 'busy' | 'rebooting';
  role: string;
  homeDir: string;
  uptime: string;
  memoryUsage: string;
  lastActive: string;
  channels: Channel[];
  // True when this agent record came from the single-agent /api/agents
  // fallback (no fleet config present). Routing through /api/fleet/<id>/
  // would 404 — the agent IS the local process, so URLs stay /api/X.
  _localOnly?: boolean;
}

export interface LogEntry {
  id: string;
  timestamp: string;
  type: 'info' | 'error' | 'success' | 'thought' | 'command' | 'system' | 'header' | 'loading' | 'tool';
  content: string;
  agentId?: string;
  channel?: string;
}

export interface Envelope {
  channel: 'telegram' | 'slack' | 'imessage' | 'dashboard';
  received_at: string;
  chat_id?: string | number;
  from?: {
    first_name?: string;
    language_code?: string;
    username?: string;
  };
  text: string;
  message_id?: string | number;
  raw?: any;
}

export interface InboxItem {
  id: string;
  direction: 'inbound' | 'outbound';
  agent: string;
  channel: 'telegram' | 'slack' | 'imessage' | 'dashboard';
  text: string;
  timestamp: string;
  status: 'processed' | 'sending' | 'sent' | 'unread' | 'read' | 'failed';
  chatId: string;
  senderName: string;
}

// Unified chronological stream — wedge W1. One row per event, four kinds
// merged from inbound/outbound envelopes + audit.db rows. See
// src/api/routes/timeline.py for the producer.
export interface TimelineEvent {
  id: string;
  ts: string;
  kind: 'inbound' | 'step' | 'advisor' | 'outbound';
  channel?: string | null;
  verdict?: string | null; // advisor only: ship | allow | hold | deny | escalate ...
  summary: string;
  detail?: string; // JSON-stringified raw row, suitable for hover/expand
  raw?: any;
}

// Outbound preview — wedge W3. One row per held draft from
// state/outbound/*.json (envelopes whose `ready_at` is still in the
// future). Operator can release, edit, or kill before the dispatching
// adapter picks the file up. See src/api/routes/outbound_preview.py.
export interface OutboundPreviewItem {
  id: string;           // = filename; stable across polls
  filename: string;
  channel: string;
  text: string;
  ready_at: string | null;
  seconds_remaining: number;
  payload: Record<string, any>;
}

export interface VirtualFile {
  path: string;
  name: string;
  type: 'file' | 'dir';
  content?: string;
  size?: string;
  updatedAt: string;
  isCustom?: boolean;
}

export interface ProjectConfig {
  productName: string;
  agentHome: string;
  apiPort: number;
  ssdPath: string;
  launcherLabelPrefix: string;
}

export interface TerminalTheme {
  id: string;
  name: string;
  bg: string;
  fg: string;
  accent: string;
  border: string;
  inputBg: string;
  bannerBg: string;
  systemText: string;
}

export const TERMINAL_THEMES: TerminalTheme[] = [
  {
    id: "sophisticated",
    name: "Sophisticated Dark",
    bg: "#050506",
    fg: "#d1d1d1",
    accent: "#a78bfa",
    border: "#252529",
    inputBg: "#0f0f11",
    bannerBg: "#141417",
    systemText: "#71717a",
  },
  {
    id: "amber",
    name: "Classic Amber",
    bg: "#110a01",
    fg: "#ffb000",
    accent: "#ffb000",
    border: "#4d2b00",
    inputBg: "#1c1100",
    bannerBg: "#1a1000",
    systemText: "#b37b00",
  },
  {
    id: "green",
    name: "Matrix Green",
    bg: "#000c02",
    fg: "#33ff33",
    accent: "#00ff66",
    border: "#004d13",
    inputBg: "#001a04",
    bannerBg: "#001403",
    systemText: "#00b33c",
  },
  {
    id: "cyberpunk",
    name: "Cyberpunk Duo",
    bg: "#0b0914",
    fg: "#00f0ff",
    accent: "#ff007f",
    border: "#2d165c",
    inputBg: "#15102a",
    bannerBg: "#120d24",
    systemText: "#745ec7",
  },
  {
    id: "solarized",
    name: "Solarized contrast",
    bg: "#002b36",
    fg: "#93a1a1",
    accent: "#268bd2",
    border: "#073642",
    inputBg: "#00212b",
    bannerBg: "#073642",
    systemText: "#586e75",
  },
  {
    id: "monochrome",
    name: "Pure Monochrome",
    bg: "#000000",
    fg: "#ffffff",
    accent: "#ffffff",
    border: "#262626",
    inputBg: "#0c0c0c",
    bannerBg: "#171717",
    systemText: "#888888",
  },
  {
    id: "office_executive",
    name: "Office Slate (Light)",
    bg: "#f8fafc",
    fg: "#0f172a",
    accent: "#2563eb",
    border: "#cbd5e1",
    inputBg: "#ffffff",
    bannerBg: "#f1f5f9",
    systemText: "#64748b",
  }
];

// Calendar event — surfaced in the Calendar tab. Server-side merge of
// state/obligations.jsonl deadlines + scheduled jobs (launchd plists)
// + optional manual events. Both the agent and the operator can write to
// state/obligations.jsonl; the calendar is the operator's read view.
export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  time?: string; // HH:MM
  description: string;
  category: "security" | "sync" | "release" | "maintenance" | "audit" | "meeting" | "deadline" | "job";
  status: "pending" | "completed" | "overdue";
  agentId?: string;
  source: "obligation" | "job" | "manual";
}

